import React from 'react';
import { Autocomplete, Chip, TextField, CircularProgress } from '@mui/material';
import { styled } from '@mui/material/styles';

// Styled component for enhanced visual effects
const StyledAutocomplete = styled(Autocomplete)(({ theme }) => ({
  '& .MuiInputBase-root': {
    transition: theme.transitions.create(['border-color', 'box-shadow', 'background-color']),
    '&:hover': {
      backgroundColor: theme.palette.mode === 'light' ? theme.palette.grey[50] : theme.palette.grey[900],
    },
    '&.Mui-focused': {
      boxShadow: `0 0 0 2px ${theme.palette.primary.light}`,
    },
  },
  '& .MuiAutocomplete-tag': {
    margin: theme.spacing(0.5),
  },
}));

/**
 * MagicAutocomplete - Enhanced autocomplete field with consistent styling and animations
 * 
 * @param {Object} props - Component properties
 * @param {string} props.name - Field name (required)
 * @param {string} props.label - Field label
 * @param {Array} props.options - Array of option objects
 * @param {any} props.value - Current value(s)
 * @param {Function} props.onChange - Change handler function
 * @param {boolean} props.multiple - Whether multiple values can be selected
 * @param {boolean} props.freeSolo - Whether to allow arbitrary values not in the options list
 * @param {boolean} props.loading - Whether options are loading
 * @param {boolean} props.required - Whether the field is required
 * @param {boolean} props.error - Whether the field has an error
 * @param {string} props.helperText - Helper text to display
 * @param {string} props.optionLabelKey - Key to use for option label (default: 'label')
 * @param {string} props.optionValueKey - Key to use for option value (default: 'value')
 * @param {Function} props.renderOption - Custom render function for options
 * @param {Function} props.renderTags - Custom render function for selected tags
 * @param {Function} props.getOptionLabel - Function to get option label
 * @param {Object} props.textFieldProps - Additional props for TextField
 */
const MagicAutocomplete = ({
  name,
  label,
  options = [],
  value,
  onChange,
  multiple = false,
  freeSolo = false,
  loading = false,
  required = false,
  error = false,
  helperText = '',
  optionLabelKey = 'label',
  optionValueKey = 'value',
  renderOption,
  renderTags,
  getOptionLabel,
  textFieldProps = {},
  ...props
}) => {
  // Handle change event
  const handleChange = (event, newValue) => {
    if (onChange) {
      onChange(newValue, name);
    }
  };

  // Normalize options format
  const normalizedOptions = options.map(option => {
    if (typeof option === 'object') {
      return {
        value: option[optionValueKey],
        label: option[optionLabelKey],
        ...option
      };
    } else {
      return {
        value: option,
        label: option
      };
    }
  });

  // Default getOptionLabel function
  const defaultGetOptionLabel = (option) => {
    if (typeof option === 'string') return option;
    if (option && typeof option === 'object') {
      return option.label || option.name || option.title || '';
    }
    return '';
  };

  // Default renderTags function
  const defaultRenderTags = (tagValues, getTagProps) => 
    tagValues.map((option, index) => (
      <Chip
        key={index}
        label={defaultGetOptionLabel(option)}
        {...getTagProps({ index })}
        size="small"
      />
    ));

  return (
    <StyledAutocomplete
      id={name}
      options={normalizedOptions}
      value={value}
      onChange={handleChange}
      multiple={multiple}
      freeSolo={freeSolo}
      getOptionLabel={getOptionLabel || defaultGetOptionLabel}
      renderTags={renderTags || defaultRenderTags}
      renderOption={renderOption || undefined}
      loading={loading}
      {...props}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          name={name}
          required={required}
          error={error}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
          {...textFieldProps}
        />
      )}
    />
  );
};

export default MagicAutocomplete;